import { FindParams } from '@readcloud/data';
import moize from 'moize';
import { PDFFindController } from 'pdfjs-dist/web/pdf_viewer';
import React from 'react';
import { Book, Rendition, Section, Spine } from '../types/epubs';
import { FindController } from '../types/FindController';
import { findStates } from '../types/SearchBarConstants';
import { Task } from '../types/Task';
import { findInSection } from '../utils/epubjsFind';

interface Match {
  cfi: string;
  excerpt: string;
  section: number;
}
interface QueryTaskState {
  query: string;
  caseSensitive: boolean;
  bookKey: string;
  spine: Spine;
  spineItemIndex: number;
  allMatches: Match[];
  itemOffset: number;
}

export const usePDFFindController = (pdfFindController?: PDFFindController) => {
  const [currentParams, setCurrentParams] = React.useState<FindParams>();
  const [totalMatchCount, setTotalMatchCount] = React.useState<number>();
  const [currentMatchCount, setCurrentMatchCount] = React.useState<number>();
  const [findState, setFindState] = React.useState<number>();

  const [controller, setController] = React.useState<FindController>();
  //set up event listeners;
  React.useEffect(() => {
    if (!pdfFindController) {
      return;
    }
    pdfFindController._eventBus.on('findbarclose', () => {
      setCurrentParams(undefined);
      pdfFindController._eventBus.dispatch('find', {
        type: undefined,
        query: '',
      });
    });
    pdfFindController._eventBus.on(
      'updatefindmatchescount',
      ({
        matchesCount,
      }: {
        matchesCount: { current: number; total: number };
      }) => {
        setTotalMatchCount(matchesCount.total);
        setCurrentMatchCount(matchesCount.current);
      }
    );
    pdfFindController._eventBus.on(
      'updatefindcontrolstate',
      ({
        matchesCount,
        state,
      }: {
        matchesCount: { current: number; total: number };
        state: number;
      }) => {
        setFindState(state);
        setTotalMatchCount(matchesCount.total);
        setCurrentMatchCount(matchesCount.current);
      }
    );
  }, [pdfFindController]);

  React.useEffect(() => {
    if (!pdfFindController) {
      return;
    }
    // create object to export that can disaptch actions for those event listeners;
    setController({
      reset: () => {
        pdfFindController._eventBus.dispatch('findbarclose', { source: this });
      },
      find: (params: FindParams) => {
        if (params) {
          setCurrentParams(params);
          pdfFindController._eventBus.dispatch('find', {
            type: undefined,
            phraseSearch: params.phraseSearch,
            caseSensitive: params.caseSensitive,
            entireWord: false,
            highlightAll: params.highlightAll,
            matchDiacritics: true,
            query: params.query,
          });
        }
      },
      findNext: () => {
        if (currentParams) {
          pdfFindController._eventBus.dispatch('find', {
            source: this,
            type: 'again',
            phraseSearch: currentParams.phraseSearch,
            caseSensitive: currentParams.caseSensitive,
            entireWord: false,
            findPrev: false,
            highlightAll: currentParams.highlightAll,
            matchDiacritics: true,
            query: currentParams.query,
          });
        }
      },
      findPrevious: () => {
        if (currentParams) {
          pdfFindController._eventBus.dispatch('find', {
            source: this,
            type: 'again',
            phraseSearch: currentParams.phraseSearch,
            caseSensitive: currentParams.caseSensitive,
            entireWord: false,
            findPrevious: true,
            highlightAll: currentParams.highlightAll,
            matchDiacritics: true,
            query: currentParams.query,
          });
        }
      },
      totalMatchCount: totalMatchCount,
      currentMatchCount: currentMatchCount,
      findState: findState,
    });
  }, [pdfFindController, totalMatchCount, currentMatchCount, findState]);

  return { controller };
};

export const useEpubFindController = (book?: Book, rendition?: Rendition) => {
  const [currentParams, setCurrentParams] = React.useState<FindParams>();
  const [totalMatchCount, setTotalMatchCount] = React.useState<number>();
  const [currentMatchCount, setCurrentMatchCount] = React.useState<number>();
  const [findState, setFindState] = React.useState<number>();
  const [controller, setController] = React.useState<FindController>();

  const matches = React.useRef<Match[]>([]);
  const highlightedMatch = React.useRef<{
    index: number;
    cfi: string;
  }>();
  const currentTask = React.useRef<Task<QueryTaskState>>();

  const memoizedFindInSection = moize(
    (bookId: string, section: Section, caseSensitive: boolean, query: string) =>
      findInSection(section, caseSensitive, query),
    {
      maxSize: 300,
    }
  );

  React.useEffect(() => {
    if (!book || !rendition) {
      setController(undefined);
      return;
    }

    const highlightColor = '#00d97e';

    const clearCurrentHighlight = () => {
      if (highlightedMatch.current?.cfi) {
        rendition?.annotations.remove(highlightedMatch.current?.cfi);
        highlightedMatch.current = { index: undefined, cfi: undefined };
      }
    };

    const clearAndHighlightMatch = async (cfi: string) => {
      await rendition.display(cfi);
      clearCurrentHighlight();
      rendition.annotations.highlight(cfi, undefined, undefined, undefined, {
        fill: highlightColor,
      });
    };

    const highlightNextMatch = async () => {
      if (!matches.current?.length) {
        return;
      }
      const nextIndex =
        highlightedMatch.current?.index !== undefined &&
        highlightedMatch.current?.index + 1 < matches.current?.length
          ? highlightedMatch.current?.index + 1
          : 0;

      const nextMatch = matches.current[nextIndex];

      const nextCfi = nextMatch?.cfi;

      if (nextIndex === highlightedMatch.current?.index) {
        return;
      }

      await clearAndHighlightMatch(nextCfi);
      highlightedMatch.current = {
        index: nextIndex,
        cfi: nextCfi,
      };
      setCurrentMatchCount(nextIndex + 1);
    };

    const highlightPreviousMatch = async () => {
      if (!matches.current?.length) {
        return;
      }
      const nextIndex =
        highlightedMatch.current.index !== undefined
          ? highlightedMatch.current.index - 1 < 0
            ? matches.current.length - 1
            : highlightedMatch.current.index - 1
          : 0;
      if (nextIndex === highlightedMatch.current.index) {
        return;
      }
      const nextMatch = matches.current[nextIndex];
      const nextCfi = nextMatch.cfi;

      await clearAndHighlightMatch(nextCfi);
      highlightedMatch.current = {
        index: nextIndex,
        cfi: nextCfi,
      };
      setCurrentMatchCount(nextIndex + 1);
    };

    setController({
      reset: () => {
        if (currentTask.current) {
          currentTask.current.pause();
        }
        clearCurrentHighlight();
        setFindState(undefined);
        setTotalMatchCount(undefined);
        setCurrentMatchCount(undefined);
      },
      find: (params: FindParams) => {
        if (params) {
          setCurrentParams(params);
          const query = params.query;

          if (!rendition?.location) {
            return;
          }
          if (query?.length < 2) {
            return;
          }
          clearCurrentHighlight();
          setFindState(findStates.PENDING);
          setTotalMatchCount(0);
          setCurrentMatchCount(0);

          const task = new Task<QueryTaskState>(
            {
              query,
              caseSensitive: params.caseSensitive,
              bookKey: book.key(),
              spine: book.spine,
              itemOffset: rendition.location.start.index,
              spineItemIndex: 0,
              allMatches: [],
            },
            async (state) => {
              if (state.spineItemIndex === state.spine.length) {
                return;
              }
              const sectionIndex =
                (state.spineItemIndex + state.itemOffset) % state.spine.length;
              const section = state.spine.get(sectionIndex);

              if (!section) {
                return;
              }

              await section.load(book.load.bind(book));

              const sectionMatches = memoizedFindInSection(
                state.bookKey,
                section,
                state.caseSensitive,
                state.query
              ).map((match) => ({ ...match, section: sectionIndex }));

              const nextState = {
                ...state,
                spineItemIndex: state.spineItemIndex + 1,
                allMatches: [...state.allMatches, ...sectionMatches],
              };

              return nextState;
            },
            {
              rateLimit: 30,
              onProgress: async (state) => {
                setFindState(
                  state.allMatches.length
                    ? findStates.FOUND
                    : findStates.PENDING
                );

                matches.current = state.allMatches;
              },
              onFinish: async (state) => {
                setFindState(
                  state.allMatches.length
                    ? findStates.FOUND
                    : findStates.NOT_FOUND
                );
                matches.current = state?.allMatches;
                setTotalMatchCount(state?.allMatches?.length);
                if (!highlightedMatch.current?.cfi) {
                  await highlightNextMatch();
                }
              },
            }
          );
          // currentTask && currentTask.finish();
          if (currentTask.current) {
            currentTask.current.pause();
          }
          currentTask.current = task;
          currentTask.current.start();
        }
      },
      findNext: async () => {
        await highlightNextMatch();
      },
      findPrevious: async () => {
        await highlightPreviousMatch();
      },
      totalMatchCount,
      currentMatchCount,
      findState,
    });
  }, [book, rendition, totalMatchCount, currentMatchCount, findState]);

  return { controller };
};
